import type React from 'react';
import styled, { useTheme } from 'styled-components';
import { useIntl } from 'react-intl';

import { getGuideColors } from 'app/features/leads/components/guide/guide-colors';

import IconAlert from 'app/assets/leads-guide/icon-alert.svg';
import IconLead from 'app/assets/leads-guide/icon-lead.svg';
import IconMail from 'app/assets/leads-guide/icon-mail.svg';
import IconSearch from 'app/assets/leads-guide/icon-search.svg';
import ChevronRightIcon from 'app/assets/chevron-right-icon.svg';
import Typography from 'app/ui/components/atoms/typography';

const StepContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme: { spacing } }) => spacing(4)};
  width: 100%;

  ${({ theme: { media, spacing } }) =>
    media.medium(
      () => `
      gap: ${spacing(8)};
    `,
    )}
`;

const Subtitle = styled(Typography)`
  text-align: center;
`;

const FlowRow = styled.div`
  display: none;
  align-items: flex-start;
  justify-content: space-between;
  width: 100%;
  padding: ${({ theme: { spacing } }) => `${spacing(3)} 0`};

  ${({ theme: { media } }) => media.medium(() => 'display: flex;')}
`;

const FLOW_ICON_AREA_HEIGHT = '5.625rem';
const FLOW_LABEL_MIN_HEIGHT = '2.75rem';

const FlowStep = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  align-items: center;
  gap: ${({ theme: { spacing } }) => spacing(1)};
  min-width: 0;
`;

const FlowIconArea = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  min-height: ${FLOW_ICON_AREA_HEIGHT};
`;

const FlowLabelArea = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: center;
  width: 100%;
  min-height: ${FLOW_LABEL_MIN_HEIGHT};
`;

const FlowLabel = styled.p<{ $accent?: boolean; $color?: string }>`
  margin: 0;
  width: 100%;
  text-align: center;
  white-space: pre-line;
  font-family: ${({ theme }) => theme.typography.poppins};
  font-weight: ${({ $accent }) => ($accent ? 600 : 400)};
  font-size: ${({ theme }) => theme.typography.xsmall};
  line-height: ${({ theme }) => theme.typography.lineHeight};
  color: ${({ theme, $accent, $color }) =>
    $color ?? ($accent ? theme.semantic.accent_500 : theme.semantic.text_small_elements)};
`;

const FlowIconCircle = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2.875rem;
  height: 2.875rem;
  border-radius: 50%;
  background-color: ${({ theme }) => theme.semantic.neutrals_300};
  color: ${({ theme }) => theme.semantic.text_small_elements};

  img {
    width: 1.875rem;
    height: 1.875rem;
  }
`;

const LeadHighlight = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  align-items: center;
  gap: ${({ theme: { spacing } }) => spacing(1)};
  min-width: 0;
  padding: 0 ${({ theme: { spacing } }) => spacing(3)};
`;

const LeadIconRing = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 5.625rem;
  height: 5.625rem;
  border-radius: 50%;
  border: 0.25rem solid ${({ theme }) => theme.semantic.accent_200};
  background-color: ${({ theme }) => theme.semantic.neutrals_200};
  box-shadow: 0 0 0 0.0625rem ${({ theme }) => theme.semantic.accent_500};

  img {
    width: 2.875rem;
    height: 3.25rem;
    flex-shrink: 0;
    object-fit: contain;
  }
`;

const FlowChevron = styled.div`
  display: flex;
  align-items: center;
  align-self: stretch;
  padding-bottom: calc(${FLOW_LABEL_MIN_HEIGHT} + ${({ theme: { spacing } }) => spacing(1)});

  img {
    width: 1.5rem;
    height: 1.5rem;
    filter: brightness(0) invert(0.7);
  }
`;

const FlowMobile = styled.div`
  display: flex;
  width: 100%;
  align-items: flex-start;

  ${({ theme: { media } }) => media.medium(() => 'display: none;')}
`;

const TimelineRail = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${({ theme: { spacing } }) => spacing(3)};
  width: 2.1875rem;
  flex-shrink: 0;
  padding: ${({ theme: { spacing } }) => `${spacing(2)} 0`};
`;

const TimelineLine = styled.div`
  position: absolute;
  left: 50%;
  top: 1.75rem;
  bottom: 1.75rem;
  width: 0.0625rem;
  background-color: ${({ theme }) => theme.semantic.neutrals_400};
  transform: translateX(-50%);
`;

const TimelineDotSlot = styled.div<{ $tall?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: ${({ $tall }) => ($tall ? '3.25rem' : '2.25rem')};
  flex-shrink: 0;
`;

const TimelineDot = styled.span`
  width: 0.5rem;
  height: 0.5rem;
  border-radius: 50%;
  background-color: ${({ theme }) => theme.semantic.neutrals_600};
  flex-shrink: 0;
`;

const MobileFlowSteps = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: ${({ theme: { spacing } }) => spacing(3)};
  min-width: 0;
  padding: ${({ theme: { spacing } }) => `${spacing(2)} 0`};
`;

const MobileFlowRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme: { spacing } }) => spacing(2)};
  padding: 0 ${({ theme: { spacing } }) => spacing(2)};
  min-height: 2.25rem;
`;

const MobileFlowRowLead = styled(MobileFlowRow)`
  min-height: 3.25rem;
`;

const MobileIconSlot = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 3.25rem;
  flex-shrink: 0;
`;

const MobileIconCircle = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2.25rem;
  height: 2.25rem;
  border-radius: 50%;
  background-color: ${({ theme }) => theme.semantic.neutrals_300};
  color: ${({ theme }) => theme.semantic.text_small_elements};

  img {
    width: 1rem;
    height: 1rem;
  }
`;

const MobileLeadIconRing = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 3.25rem;
  height: 3.25rem;
  border-radius: 50%;
  border: 0.1875rem solid ${({ theme }) => theme.semantic.accent_200};
  background-color: ${({ theme }) => theme.semantic.neutrals_200};
  box-shadow: 0 0 0 0.0625rem ${({ theme }) => theme.semantic.accent_500};

  img {
    width: 1.5rem;
    height: 1.6875rem;
    flex-shrink: 0;
    object-fit: contain;
  }
`;

const MobileFlowLabel = styled.p<{ $accent?: boolean; $color?: string }>`
  margin: 0;
  font-family: ${({ theme }) => theme.typography.poppins};
  font-weight: ${({ $accent }) => ($accent ? 600 : 400)};
  font-size: ${({ theme }) => theme.typography.small};
  line-height: ${({ theme }) => theme.typography.lineHeight};
  color: ${({ theme, $accent, $color }) =>
    $color ?? ($accent ? theme.semantic.accent_500 : theme.semantic.text_small_elements)};
`;

const ComplianceCard = styled.div`
  display: flex;
  gap: ${({ theme: { spacing } }) => spacing(2)};
  padding: ${({ theme: { spacing } }) => spacing(2)};
  border-radius: ${({ theme: { spacing } }) => spacing(2)};
  border: 0.0625rem solid ${({ theme }) => theme.semantic.neutrals_400};
  background-color: ${({ theme }) => theme.semantic.neutrals_200};
  width: 100%;

  img {
    width: 1.25rem;
    height: 1.25rem;
    flex-shrink: 0;
  }

  ${({ theme: { media, spacing } }) =>
    media.medium(
      () => `
      gap: ${spacing(3)};
      padding: ${spacing(3)};

      img {
        width: 1.5rem;
        height: 1.5rem;
      }
    `,
    )}
`;

const ComplianceText = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme: { spacing } }) => spacing(1)};

  ${({ theme: { media, spacing } }) => media.medium(() => `gap: ${spacing(2)};`)}
`;

const ComplianceTitle = styled(Typography)`
  font-weight: ${({ theme }) => theme.typography.bold};
  font-size: ${({ theme }) => theme.typography.xsmall};
  line-height: 1.4;
`;

const ComplianceBody = styled(Typography)`
  font-size: ${({ theme }) => theme.typography.xsmall};
  line-height: 1.4;
`;

const formatFlowLabel = (label: string) => label.replace(/\n/g, ' ');

export const HowItWorksStep: React.FC = () => {
  const intl = useIntl();
  const { semantic } = useTheme();
  const guideColors = getGuideColors(semantic);

  const subtitle = intl.formatMessage({ id: 'leadsGuide.step1.subtitle' });
  const athleteSearches = intl.formatMessage({ id: 'leadsGuide.step1.flow.athleteSearches' });
  const submitsEnquiry = intl.formatMessage({ id: 'leadsGuide.step1.flow.submitsEnquiry' });
  const leadInHub = intl.formatMessage({ id: 'leadsGuide.step1.flow.leadInHub' });
  const youFollowUp = intl.formatMessage({ id: 'leadsGuide.step1.flow.youFollowUp' });
  const complianceTitle = intl.formatMessage({ id: 'leadsGuide.step1.compliance.title' });
  const complianceBody = intl.formatMessage({ id: 'leadsGuide.step1.compliance.body' });

  const mobileAthleteSearches = formatFlowLabel(athleteSearches);
  const mobileSubmitsEnquiry = formatFlowLabel(submitsEnquiry);
  const mobileYouFollowUp = formatFlowLabel(youFollowUp);

  return (
    <StepContent data-testid="leads-guide-step-how-it-works">
      <Subtitle variant="h5">{subtitle}</Subtitle>
      <FlowMobile data-testid="leads-guide-flow-mobile">
        <TimelineRail aria-hidden={true}>
          <TimelineLine />
          <TimelineDotSlot>
            <TimelineDot />
          </TimelineDotSlot>
          <TimelineDotSlot>
            <TimelineDot />
          </TimelineDotSlot>
          <TimelineDotSlot $tall={true}>
            <TimelineDot />
          </TimelineDotSlot>
          <TimelineDotSlot>
            <TimelineDot />
          </TimelineDotSlot>
        </TimelineRail>
        <MobileFlowSteps>
          <MobileFlowRow>
            <MobileIconSlot>
              <MobileIconCircle>
                <img src={IconSearch} alt="" />
              </MobileIconCircle>
            </MobileIconSlot>
            <MobileFlowLabel $color={guideColors.textPrimary}>{mobileAthleteSearches}</MobileFlowLabel>
          </MobileFlowRow>
          <MobileFlowRow>
            <MobileIconSlot>
              <MobileIconCircle>
                <img src={IconMail} alt="" />
              </MobileIconCircle>
            </MobileIconSlot>
            <MobileFlowLabel $color={guideColors.textPrimary}>{mobileSubmitsEnquiry}</MobileFlowLabel>
          </MobileFlowRow>
          <MobileFlowRowLead>
            <MobileIconSlot>
              <MobileLeadIconRing>
                <img src={IconLead} alt="" />
              </MobileLeadIconRing>
            </MobileIconSlot>
            <MobileFlowLabel $accent={true} $color={guideColors.textAccent}>
              {leadInHub}
            </MobileFlowLabel>
          </MobileFlowRowLead>
          <MobileFlowRow>
            <MobileIconSlot>
              <MobileIconCircle>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden={true}>
                  <path
                    d="M5 12.5L9.5 17L19 7"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </MobileIconCircle>
            </MobileIconSlot>
            <MobileFlowLabel $color={guideColors.textPrimary}>{mobileYouFollowUp}</MobileFlowLabel>
          </MobileFlowRow>
        </MobileFlowSteps>
      </FlowMobile>
      <FlowRow data-testid="leads-guide-flow-desktop">
        <FlowStep>
          <FlowIconArea>
            <FlowIconCircle>
              <img src={IconSearch} alt="" />
            </FlowIconCircle>
          </FlowIconArea>
          <FlowLabelArea>
            <FlowLabel $color={guideColors.textPrimary}>{athleteSearches}</FlowLabel>
          </FlowLabelArea>
        </FlowStep>
        <FlowChevron aria-hidden={true}>
          <img src={ChevronRightIcon} alt="" />
        </FlowChevron>
        <FlowStep>
          <FlowIconArea>
            <FlowIconCircle>
              <img src={IconMail} alt="" />
            </FlowIconCircle>
          </FlowIconArea>
          <FlowLabelArea>
            <FlowLabel $color={guideColors.textPrimary}>{submitsEnquiry}</FlowLabel>
          </FlowLabelArea>
        </FlowStep>
        <FlowChevron aria-hidden={true}>
          <img src={ChevronRightIcon} alt="" />
        </FlowChevron>
        <LeadHighlight>
          <FlowIconArea>
            <LeadIconRing>
              <img src={IconLead} alt="" />
            </LeadIconRing>
          </FlowIconArea>
          <FlowLabelArea>
            <FlowLabel $accent={true} $color={guideColors.textAccent}>
              {leadInHub}
            </FlowLabel>
          </FlowLabelArea>
        </LeadHighlight>
        <FlowChevron aria-hidden={true}>
          <img src={ChevronRightIcon} alt="" />
        </FlowChevron>
        <FlowStep>
          <FlowIconArea>
            <FlowIconCircle>
              <svg width="30" height="30" viewBox="0 0 24 24" fill="none" aria-hidden={true}>
                <path
                  d="M5 12.5L9.5 17L19 7"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </FlowIconCircle>
          </FlowIconArea>
          <FlowLabelArea>
            <FlowLabel $color={guideColors.textPrimary}>{youFollowUp}</FlowLabel>
          </FlowLabelArea>
        </FlowStep>
      </FlowRow>
      <ComplianceCard data-testid="leads-guide-data-ownership">
        <img src={IconAlert} alt="" />
        <ComplianceText>
          <ComplianceTitle variant="body2" color={guideColors.textAccent}>
            {complianceTitle}
          </ComplianceTitle>
          <ComplianceBody variant="body2" color={guideColors.textComplianceBody}>
            {complianceBody}
          </ComplianceBody>
        </ComplianceText>
      </ComplianceCard>
    </StepContent>
  );
};
